import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaArrowRight, FaEye, FaUser } from 'react-icons/fa';
import { LuDot } from 'react-icons/lu';
import Loader from '../../components/Loader';
import Navbar from './Navbar';
import Calling from '../../api/Calling';
import NotFound from '../../components/ul/NotFound';
import MainButton from '../../components/ul/MainButton';
import formatDateTime from '../../components/formatDateTime';
import getISTDate from '../../components/getISTDate';

const UserCome = () => {
    const [loader, setLoader] = useState(false);
    const [data, setData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalUser, setTotalUser] = useState(0);
    const [startDate, setStartDate] = useState(getISTDate());
    const [endDate, setEndDate] = useState(getISTDate());
    const [selectState, setSelectState] = useState('today');

    const limit = 2; // Items per page
    const navigate = useNavigate();

    // Fetch data from API
    const fetchData = async () => {
        setLoader(true);
        try {
            const response = await Calling.getTodayUniqueQuizPlayerAge(currentPage, limit, startDate, endDate);
            if (response?.status) {
                setData(response?.data);
                setTotalPages(response?.totalPage);
                setTotalUser(response?.totalRecord);
            } else {
                toast.error(response?.message);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to fetch data');
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, [currentPage, startDate, endDate,]);

    const handleViewUser = (id) => {
        navigate(`/manage/user/all/info/${id}`);
    };

    const handleFormDate = (e) => {
        if (e.target.name === "startDate") {
            setStartDate(e.target.value);
            setSelectState('range');
        } else {
            setEndDate(e.target.value);
            setSelectState('range');

        }
    }


 const handleDateRangeFilter = (range) => {
        const todayUTC = new Date();

        // Convert any UTC date to IST (UTC +5:30) and return YYYY-MM-DD
        const toIST = (date) => {
            const istOffset = 5.5 * 60 * 60 * 1000; // 5.5 hours in ms
            return new Date(date.getTime() + istOffset).toISOString().split('T')[0];
        };

        let start, end;

        switch (range) {
            case 'today':
                start = toIST(todayUTC);
                end = start;
                setSelectState('today');
                break;

            case 'yesterday':
                const yesterdayUTC = new Date(todayUTC);
                yesterdayUTC.setDate(todayUTC.getDate() - 1);
                start = toIST(yesterdayUTC);
                end = start;
                setSelectState('yesterday');
                break;

            case 'thisWeek':
                const firstDayOfWeekUTC = new Date(todayUTC);
                firstDayOfWeekUTC.setDate(todayUTC.getDate() - todayUTC.getDay());
                const lastDayOfWeekUTC = new Date(firstDayOfWeekUTC);
                lastDayOfWeekUTC.setDate(firstDayOfWeekUTC.getDate() + 6);
                start = toIST(firstDayOfWeekUTC);
                end = toIST(lastDayOfWeekUTC);
                setSelectState('thisWeek');
                break;

            case 'thisMonth':
                const firstDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth(), 1);
                const lastDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayOfMonthUTC);
                end = toIST(lastDayOfMonthUTC);
                setSelectState('thisMonth');
                break;

            case 'last6Months':
                const firstDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() - 5, 1);
                const lastDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayLast6UTC);
                end = toIST(lastDayLast6UTC);
                setSelectState('last6Months');
                break;

            case 'thisYear':
                const firstDayOfYearUTC = new Date(todayUTC.getFullYear(), 0, 1);
                const lastDayOfYearUTC = new Date(todayUTC.getFullYear(), 11, 31);
                start = toIST(firstDayOfYearUTC);
                end = toIST(lastDayOfYearUTC);
                setSelectState('thisYear');
                break;

            default:
                start = '';
                end = '';
        }

        setStartDate(start);
        setEndDate(end);
        setCurrentPage(1); // Reset page when filter changes
    };


    return (
        <>
            <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
                <h1 className="text-xl md:text-2xl font-bold mt-4 text-main-font w-auto">Active Users</h1>
                <div className='bg-main-white rounded-xl'>
                    <Navbar selectState={'comeback'} />
                    <hr className='border-main-border' />

                    {/* Total Users Card */}
                    <div className="p-4 bg-main-th mx-4 my-5 rounded-xl">
                        <h2 className="text-base md:text-lg text-main-subfont">Active Users</h2>
                        <p className="text-lg md:text-xl text-main-font">
                            {loader ? (
                                <span className="flex items-center text-main-subfont">
                                    <svg className="animate-spin h-5 w-5 mr-2 text-main-subfont" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Loading...
                                </span>
                            ) : (
                                totalUser < 10 ? '0' + totalUser : totalUser
                            )}
                        </p>
                    </div>
                    <hr className='border-main-border border-[2px]' />

                    {/* Date Range Filters */}
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between w-full space-y-4 lg:space-y-0 lg:space-x-4 px-4 py-3">
                        {/* Buttons Container - Better Responsive Layout */}
                        <div className="flex flex-wrap gap-2 justify-start">
                            <MainButton
                                onClick={() => handleDateRangeFilter('today')}
                                label={'Today'}
                                selectState={selectState === 'today'}
                            />
                            <MainButton
                                onClick={() => handleDateRangeFilter('yesterday')}
                                label={'Yesterday'}
                                selectState={selectState === "yesterday"}
                            />
                            <MainButton
                                onClick={() => handleDateRangeFilter('thisWeek')}
                                label={'This Week'}
                                selectState={selectState === 'thisWeek'}
                            />
                            <MainButton
                                onClick={() => handleDateRangeFilter('thisMonth')}
                                label={'This Month'}
                                selectState={selectState === 'thisMonth'}
                            />
                            <MainButton
                                onClick={() => handleDateRangeFilter('last6Months')}
                                label={'Last 6 Months'}
                                selectState={selectState === 'last6Months'}
                            />
                            <MainButton
                                onClick={() => handleDateRangeFilter('thisYear')}
                                label={'This Year'}
                                selectState={selectState === 'thisYear'}
                            />
                        </div>

                        {/* Date Input Container - Improved Responsive Layout */}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 lg:w-auto lg:flex-nowrap lg:whitespace-nowrap">
                            <div className="flex-1 lg:w-auto">
                                <input
                                    type="date"
                                    value={startDate}
                                    name="startDate"
                                    onChange={handleFormDate}
                                    placeholder="Start Date"
                                    className={`border cursor-pointer bg-main-white w-full ${selectState === "range"
                                        ? 'border-main-blue text-main-blue'
                                        : 'border-main-border text-main-subfont'
                                        } px-4 py-2 rounded-lg focus:border-main-blue focus:outline-none shadow-sm`}
                                />
                            </div>
                            <div className="flex-1 lg:w-auto">
                                <input
                                    type="date"
                                    value={endDate}
                                    name="endDate"
                                    onChange={handleFormDate}
                                    placeholder="End Date"
                                    className={`border cursor-pointer bg-main-white w-full ${selectState === "range"
                                        ? 'border-main-blue text-main-blue'
                                        : 'border-main-border text-main-subfont'
                                        } px-4 py-2 rounded-lg focus:border-main-blue focus:outline-none shadow-sm`}
                                />
                            </div>
                        </div>
                    </div>

                    <hr className='bg-main-border/10 mx-4' />


                    {/* Table of Users - Responsive Overflow */}
                    <div className="overflow-x-auto">
                        <table className='w-full my-5 text-left'>
                            <thead className='bg-main-th text-main-font uppercase'>
                                <tr>
                                    <th className="ps-4 py-3 text-left text-xs font-medium rounded-tl-lg">#</th>
                                    <th className="p-2 text-xs font-medium">User ID</th>
                                    <th className="p-2 text-xs font-medium">Account Age (Days)</th>
                                    <th className="p-2 text-xs font-medium">Account Created</th>
                                    <th className="p-2 text-xs font-medium">Last Played</th>
                                    <th className="p-2 text-xs font-medium">Quiz Type</th>
                                    <th className="p-2 text-xs font-medium">Quiz Name</th>
                                    <th className="p-2 text-xs font-medium rounded-tr-lg">Actions</th>
                                </tr>
                            </thead>

                            {loader ? (
                                <tbody>
                                    <tr>
                                        <td colSpan={8} className="text-center py-4">
                                            <Loader loader={loader} />
                                        </td>
                                    </tr>
                                </tbody>
                            ) : (
                                <tbody>
                                    {data.length > 0 ? data.map((user, index) => (
                                        <tr key={user._id} className='hover:bg-main-thHover'>
                                            <td className="ps-4">{index + 1 + (currentPage - 1) * limit}</td>
                                            <td className="p-2">{user.userId}</td>
                                            <td className="p-2">
                                                <span className={`px-3 py-1 w-max text-sm rounded-full flex items-center
                                                    ${user.daysOld === 0 ? 'bg-green-100 text-green-600' :
                                                        user.daysOld < 30 ? 'bg-blue-100 text-blue-600' :
                                                            user.daysOld < 90 ? 'bg-yellow-100 text-yellow-600' :
                                                                'bg-red-100 text-red-600'}`}>
                                                    <LuDot size={20} /> {user.daysOld === 0 ? `New` : `${user.daysOld} days`}
                                                </span>
                                            </td>
                                            <td className="p-2">{formatDateTime(user.accountCreatedAt)}</td>
                                            <td className="p-2">{formatDateTime(user.todayPlayedAt)}</td>
                                            <td className="p-2 capitalize">{user.type || 'N/A'}</td>
                                            <td className="p-2">{user.quizName || 'N/A'}</td>
                                            <td className="p-2">
                                                <button
                                                    onClick={() => handleViewUser(user?.userId)}
                                                    className="bg-main-blue text-main-white px-2 py-1 rounded hover:bg-main-blue/80 shadow-lg"
                                                >
                                                    <FaUser />
                                                </button>
                                            </td>
                                        </tr>
                                    )) : <tr><td className='' colSpan={8}><NotFound /></td></tr>}
                                </tbody>
                            )}
                        </table>
                    </div>

                    {/* Pagination - Responsive Layout */}
                    {totalPages > 1 && (
                        <div className="flex flex-col md:flex-row justify-center items-center p-6 relative">
                            <div className="flex gap-6 mb-4 md:mb-0">
                                <button
                                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className={`px-2 w-[110px] flex items-center justify-center gap-2 py-2 rounded-lg ${currentPage === 1
                                        ? "bg-main-white border border-main-border text-main-subfont"
                                        : "bg-main-blue text-main-white"
                                        }`}
                                >
                                    <FaArrowLeft /> Previous
                                </button>
                                <button
                                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className={`px-2 w-[110px] flex items-center justify-center gap-2 py-2 rounded-lg ${currentPage === totalPages
                                        ? "bg-main-white border border-main-border text-main-subfont"
                                        : "bg-main-blue text-main-white"
                                        }`}
                                >
                                    Next <FaArrowRight />
                                </button>
                            </div>

                            <p className="text-main-subfont md:absolute md:right-6 mt-2 md:mt-0">
                                <span className="border border-main-border text-main-font px-2 py-1 rounded-lg">
                                    {currentPage <= 9 ? "0" + currentPage : currentPage}
                                </span>{" "}
                                of {totalPages <= 9 ? "0" + totalPages : totalPages}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default UserCome;

